import { useEffect, type ReactNode } from "react";
import { demoLogin, fetchMe, logout } from "@/api/auth";
import { fetchMeta } from "@/api/meta";
import { useAuthStore } from "@/state/auth.store";

/**
 * On mount: learn demo mode, then resolve the session. With a token we load the
 * current user; if that fails (e.g. after an hourly demo reset invalidated it) or
 * there was no token, we auto-login as the demo employee when demo mode is on.
 */
export function AuthBootstrap({ children }: { children: ReactNode }) {
  const { token, setUser, setHydrated } = useAuthStore();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let demo = false;
      try {
        const meta = await fetchMeta();
        demo = !!meta.demo;
      } catch {
        /* ignore — assume not demo */
      }
      if (cancelled) return;
      useAuthStore.getState().setDemo(demo);

      if (token) {
        try {
          const user = await fetchMe();
          if (!cancelled) {
            setUser(user);
            setHydrated(true);
          }
          return;
        } catch {
          logout();
          useAuthStore.getState().logout(); // clear the stale token, then maybe re-auto-login
        }
      }
      if (cancelled) return;

      if (demo) {
        try {
          const res = await demoLogin();
          if (!cancelled) useAuthStore.getState().setSession(res.token, res.user);
        } catch {
          /* demo login unavailable — fall back to the public landing */
        }
      }
      if (!cancelled) setHydrated(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
}
